import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

@Component({
  selector: 'app-toast-notification',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './toast-notification.component.html',
  styleUrls: ['./toast-notification.component.css'],
  animations: [
    trigger('toastAnimation', [
      state(
        'hidden',
        style({
          opacity: 0,
          transform: 'translateX(100%)',
        })
      ),
      state(
        'visible',
        style({
          opacity: 1,
          transform: 'translateX(0)',
        })
      ),
      transition('hidden => visible', animate('300ms ease-in')),
      transition('visible => hidden', animate('300ms ease-out')),
    ]),
  ],
})
export class ToastNotificationComponent implements OnInit, OnDestroy {
  /** Mensaje a mostrar en la notificación toast */
  @Input() message: string = '';

  /** Tipo de notificación toast (success, error, info, warning) */
  @Input() type: ToastType = 'info';

  /** Duración en milisegundos que se mostrará la notificación */
  @Input() duration: number = 3000;

  /** Estado de la animación del toast (visible u oculto) */
  animationState: 'visible' | 'hidden' = 'hidden';

  /** ID del timeout para auto-ocultar el toast */
  private timeoutId: any;

  /**
   * Inicializa el componente mostrando la notificación
   */
  ngOnInit(): void {
    this.show();
  }

  /**
   * Limpia el timeout al destruir el componente
   */
  ngOnDestroy(): void {
    this.clearTimeout();
  }

  /**
   * Muestra la notificación toast y configura el auto-ocultado
   */
  show(): void {
    this.animationState = 'visible';
    this.setAutoHide();
  }

  /**
   * Oculta la notificación toast
   */
  hide(): void {
    this.animationState = 'hidden';
  }

  /**
   * Configura el timeout para ocultar automáticamente el toast
   * después de la duración especificada
   */
  private setAutoHide(): void {
    this.clearTimeout();
    if (this.duration > 0) {
      this.timeoutId = setTimeout(() => {
        this.hide();
      }, this.duration);
    }
  }

  /**
   * Limpia el timeout existente si hay alguno
   */
  private clearTimeout(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  /**
   * Maneja el evento de cierre manual del toast
   */
  onClose(): void {
    this.clearTimeout();
    this.hide();
  }

  /**
   * Obtiene la clase de icono correspondiente según el tipo de notificación
   * @returns Clase CSS del icono de Bootstrap
   */
  get iconClass(): string {
    switch (this.type) {
      case 'success':
        return 'bi bi-check-circle-fill';
      case 'error':
        return 'bi bi-exclamation-circle-fill';
      case 'warning':
        return 'bi bi-exclamation-triangle-fill';
      case 'info':
      default:
        return 'bi bi-info-circle-fill';
    }
  }
}
